/**
 * Repair Confidence Scoring — Quality signal on completed work.
 *
 * Pure functions that score how confident we are that a repair will hold,
 * based on observable signals from the work order data. This helps PMs
 * prioritize follow-ups and techs build quality track records.
 *
 * Signals analyzed:
 * - Was the issue recurring? (indicates prior fixes didn't hold)
 * - How long was the WO open? (rushed vs thorough)
 * - Were parts used? (parts-less repairs are sometimes band-aids)
 * - Did the tech leave notes? (documentation signals thoroughness)
 * - Has the same issue reopened after this fix?
 * - Tech's historical reliability on similar issues
 */

import { isCompletedWO } from './dataQueries';

// ─── Signal Analysis ─────────────────────────────────────────────────────────

/**
 * Analyze fix quality signals for a completed work order.
 * Returns individual signal scores and an overall confidence.
 *
 * @param {Object} wo - The work order to analyze
 * @param {Array} allWorkOrders - All work orders (for pattern detection)
 * @param {Array} laborEntries - Labor entries for notes analysis
 * @param {Array} partsUsage - Parts usage records
 * @returns {Object} { confidence, signals, recommendation }
 */
export function scoreRepairConfidence(wo, allWorkOrders, laborEntries, partsUsage) {
  if (!wo) return { confidence: 0, level: 'unknown', signals: [], recommendation: 'No work order data' };

  const signals = [];
  let totalWeight = 0;
  let weightedScore = 0;

  // Signal 1: Parts used (weight: 20)
  const partsSignal = analyzePartsUsage(wo, partsUsage);
  signals.push(partsSignal);
  weightedScore += partsSignal.score * partsSignal.weight;
  totalWeight += partsSignal.weight;

  // Signal 2: Labor notes quality (weight: 15)
  const notesSignal = analyzeNotesQuality(wo, laborEntries);
  signals.push(notesSignal);
  weightedScore += notesSignal.score * notesSignal.weight;
  totalWeight += notesSignal.weight;

  // Signal 3: Time spent (weight: 15)
  const timeSignal = analyzeTimeSpent(wo, laborEntries);
  signals.push(timeSignal);
  weightedScore += timeSignal.score * timeSignal.weight;
  totalWeight += timeSignal.weight;

  // Signal 4: Repeat issue (weight: 30) — highest weight
  const repeatSignal = analyzeRepeatRisk(wo, allWorkOrders);
  signals.push(repeatSignal);
  weightedScore += repeatSignal.score * repeatSignal.weight;
  totalWeight += repeatSignal.weight;

  // Signal 5: Post-fix reopening (weight: 20)
  const reopenSignal = analyzeReopenHistory(wo, allWorkOrders);
  signals.push(reopenSignal);
  weightedScore += reopenSignal.score * reopenSignal.weight;
  totalWeight += reopenSignal.weight;

  const confidence = totalWeight > 0 ? Math.round(weightedScore / totalWeight) : 0;
  const level = confidence >= 75 ? 'high' : confidence >= 50 ? 'medium' : 'low';

  const lowSignals = signals.filter(s => s.score < 50);
  const recommendation = lowSignals.length === 0
    ? 'Repair looks solid — no red flags'
    : `Watch: ${lowSignals.map(s => s.name).join(', ')}`;

  return { confidence, level, signals, recommendation };
}

// ─── Individual Signal Analyzers ─────────────────────────────────────────────

/**
 * Did the tech use parts? Parts-less repairs on hardware issues are suspicious.
 */
export function analyzePartsUsage(wo, partsUsage) {
  const signal = { name: 'Parts Used', weight: 20, score: 50, detail: '' };

  if (!Array.isArray(partsUsage)) return { ...signal, detail: 'No parts data available' };

  const woParts = partsUsage.filter(p =>
    p.woId === wo.id || p.woNumber === wo.id || p.woId === wo.woNumber || p.woNumber === wo.woNumber
  );

  if (woParts.length > 0) {
    signal.score = 80;
    signal.detail = `${woParts.length} part(s) used`;
  } else {
    // Parts-less repair — could be fine (clog, adjustment) or bad (band-aid fix)
    const desc = (wo.description || '').toLowerCase();
    const likelyNoParts = ['clog', 'adjust', 'reset', 'clean', 'inspect', 'unclog', 'test'].some(w => desc.includes(w));
    signal.score = likelyNoParts ? 70 : 30;
    signal.detail = likelyNoParts
      ? 'No parts needed (task type doesn\'t typically require parts)'
      : 'No parts used — possible band-aid fix';
  }

  return signal;
}

/**
 * Did the tech leave detailed notes? Good notes = thorough work.
 */
export function analyzeNotesQuality(wo, laborEntries) {
  const signal = { name: 'Documentation', weight: 15, score: 50, detail: '' };

  if (!Array.isArray(laborEntries)) return { ...signal, detail: 'No labor data available' };

  const woLabor = laborEntries.filter(e =>
    e.woId === wo.id || e.woNumber === wo.id || e.woId === wo.woNumber || e.woNumber === wo.woNumber
  );

  if (woLabor.length === 0) {
    signal.score = 30;
    signal.detail = 'No labor entries found';
    return signal;
  }

  // Check for notes/description quality
  const allNotes = woLabor
    .map(e => e.description || e.notes || '')
    .join(' ')
    .trim();

  if (allNotes.length === 0) {
    signal.score = 30;
    signal.detail = 'No notes left by tech';
  } else if (allNotes.length < 20) {
    signal.score = 50;
    signal.detail = 'Minimal notes';
  } else if (allNotes.length < 80) {
    signal.score = 70;
    signal.detail = 'Adequate notes';
  } else {
    signal.score = 90;
    signal.detail = 'Detailed documentation';
  }

  return signal;
}

/**
 * Was enough time spent? Very short times on complex issues are concerning.
 */
export function analyzeTimeSpent(wo, laborEntries) {
  const signal = { name: 'Time Invested', weight: 15, score: 50, detail: '' };

  if (!Array.isArray(laborEntries)) return { ...signal, detail: 'No labor data available' };

  const woLabor = laborEntries.filter(e =>
    e.woId === wo.id || e.woNumber === wo.id || e.woId === wo.woNumber || e.woNumber === wo.woNumber
  );

  const totalHours = woLabor.reduce((sum, e) => sum + (e.hours || 0), 0);

  if (totalHours === 0) {
    signal.score = 40;
    signal.detail = 'No time logged';
  } else if (totalHours <= 0.5) {
    signal.score = 45;
    signal.detail = `Only ${totalHours}h logged — very quick fix`;
  } else if (totalHours <= 4) {
    signal.score = 75;
    signal.detail = `${totalHours}h logged — reasonable`;
  } else {
    signal.score = 65;
    signal.detail = `${totalHours}h logged — extended repair`;
  }

  return signal;
}

/**
 * Has this issue occurred before at this unit? If so, prior fixes didn't hold.
 */
export function analyzeRepeatRisk(wo, allWorkOrders) {
  const signal = { name: 'Repeat Risk', weight: 30, score: 80, detail: '' };

  if (!Array.isArray(allWorkOrders) || !wo.unitId) return { ...signal, detail: 'Cannot assess' };

  const desc = wo.description || '';
  const keywords = extractRepairKeywords(desc);
  if (keywords.length === 0) return { ...signal, detail: 'Cannot classify issue type' };

  // Find prior similar WOs at this unit (before this WO's creation date)
  const priorWOs = allWorkOrders.filter(other =>
    other.unitId === wo.unitId &&
    other.id !== wo.id &&
    other.description &&
    (!wo.createdDate || !other.createdDate || other.createdDate < wo.createdDate)
  );

  const similarPrior = priorWOs.filter(other => {
    const otherKeywords = extractRepairKeywords(other.description);
    const overlap = keywords.filter(k => otherKeywords.includes(k));
    return overlap.length >= Math.ceil(keywords.length * 0.4);
  });

  if (similarPrior.length === 0) {
    signal.score = 90;
    signal.detail = 'First time this issue occurred';
  } else if (similarPrior.length === 1) {
    signal.score = 55;
    signal.detail = 'Issue has occurred once before';
  } else {
    signal.score = Math.max(10, 50 - (similarPrior.length * 10));
    signal.detail = `Issue has occurred ${similarPrior.length} times before — chronic problem`;
  }

  return signal;
}

/**
 * After this WO was closed, did a similar issue open again?
 * This is the strongest signal that a repair didn't hold.
 */
export function analyzeReopenHistory(wo, allWorkOrders) {
  const signal = { name: 'Post-Fix Stability', weight: 20, score: 75, detail: '' };

  if (!Array.isArray(allWorkOrders) || !wo.unitId || !isCompletedWO(wo)) {
    signal.detail = !isCompletedWO(wo) ? 'WO still open' : 'Cannot assess';
    signal.score = 50;
    return signal;
  }

  const desc = wo.description || '';
  const keywords = extractRepairKeywords(desc);
  if (keywords.length === 0) return { ...signal, detail: 'Cannot classify issue type' };

  // Find WOs created AFTER this one was completed at the same unit
  const subsequentWOs = allWorkOrders.filter(other =>
    other.unitId === wo.unitId &&
    other.id !== wo.id &&
    other.description &&
    other.createdDate && wo.createdDate &&
    other.createdDate > wo.createdDate
  );

  const similarSubsequent = subsequentWOs.filter(other => {
    const otherKeywords = extractRepairKeywords(other.description);
    const overlap = keywords.filter(k => otherKeywords.includes(k));
    return overlap.length >= Math.ceil(keywords.length * 0.4);
  });

  if (similarSubsequent.length === 0) {
    signal.score = 90;
    signal.detail = 'No similar issues after this fix';
  } else {
    signal.score = Math.max(10, 60 - (similarSubsequent.length * 20));
    signal.detail = `${similarSubsequent.length} similar issue(s) appeared after this fix`;
  }

  return signal;
}

// ─── Tech Reliability ────────────────────────────────────────────────────────

/**
 * Calculate a tech's reliability score based on their repair history.
 * Higher = their fixes tend to hold longer.
 */
export function getTechReliabilityScore(techName, allWorkOrders, laborEntries, partsUsage) {
  if (!techName || !Array.isArray(allWorkOrders)) return { score: 50, level: 'unknown', totalWOs: 0 };

  // Find WOs where this tech was involved
  const techWOs = allWorkOrders.filter(wo =>
    wo.tech?.toLowerCase().includes(techName.toLowerCase()) && isCompletedWO(wo)
  );

  if (techWOs.length === 0) return { score: 50, level: 'unknown', totalWOs: 0 };

  // Score a sample of their work
  const sampled = techWOs.slice(0, 50); // Cap for performance
  let totalConfidence = 0;

  for (const wo of sampled) {
    const result = scoreRepairConfidence(wo, allWorkOrders, laborEntries || [], partsUsage || []);
    totalConfidence += result.confidence;
  }

  const avgConfidence = Math.round(totalConfidence / sampled.length);
  const level = avgConfidence >= 75 ? 'excellent' : avgConfidence >= 60 ? 'good' : avgConfidence >= 45 ? 'fair' : 'needs_improvement';

  return {
    score: avgConfidence,
    level,
    totalWOs: techWOs.length,
    sampledWOs: sampled.length,
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Extract repair-relevant keywords from a description.
 * More focused than the predictiveEngine version — targets repair categories.
 */
function extractRepairKeywords(description) {
  if (!description || typeof description !== 'string') return [];

  return description
    .toLowerCase()
    .replace(/[^a-z0-9\s/-]/g, '')
    .split(/\s+/)
    .filter(w => w.length > 2)
    .filter(w => !['the', 'and', 'for', 'not', 'with', 'has', 'are', 'was', 'this', 'that', 'please', 'need', 'needs', 'unit', 'tenant'].includes(w))
    .filter((w, i, arr) => arr.indexOf(w) === i);
}

/**
 * Get a summary of repair confidence across all closed WOs for a unit.
 */
export function getUnitRepairSummary(unitId, allWorkOrders, laborEntries, partsUsage) {
  if (!unitId || !Array.isArray(allWorkOrders)) {
    return { avgConfidence: 0, totalClosed: 0, lowConfidenceCount: 0, repairs: [] };
  }

  const closedWOs = allWorkOrders.filter(wo =>
    wo.unitId === unitId && isCompletedWO(wo)
  );

  if (closedWOs.length === 0) {
    return { avgConfidence: 0, totalClosed: 0, lowConfidenceCount: 0, repairs: [] };
  }

  const repairs = closedWOs.map(wo => {
    const result = scoreRepairConfidence(wo, allWorkOrders, laborEntries || [], partsUsage || []);
    return { woId: wo.id, description: wo.description, ...result };
  });

  const avgConfidence = Math.round(
    repairs.reduce((sum, r) => sum + r.confidence, 0) / repairs.length
  );

  return {
    avgConfidence,
    totalClosed: closedWOs.length,
    lowConfidenceCount: repairs.filter(r => r.confidence < 50).length,
    repairs: repairs.sort((a, b) => a.confidence - b.confidence),
  };
}
